import React, { useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiPlusCircle } from '@mdi/js';
import { Box, Menu, MenuItem, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import PopupTitle from '../common/popup_title';
import MealAdvancedSearchPopover from '../meal/meal_advanced_search_popover';
import { dayIndexesWithStartDay } from '../../services/planner';
import {
  useCreateContentEntryMutation,
  useGetProgrammesQuery,
  useGetSpaceQuery,
  useUpdateProgrammeMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  programmePlanDayIDFromDayIndex,
  programmePlanWithEntryAdded,
  programmesComparer,
  programmeWithPlanUpdated,
} from '../../services/programmes';
import { useTracker } from '../../operations/tracking_operations';
import { ActivityObjectType, EntryType } from '../../API';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { healthProGroupForSpaceID } from '../../services/users';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const AddProgrammePlanCardLink = ({ laneId }) => {
  const theme = useTheme();

  const tracker = useTracker();

  const navigate = useNavigate();

  const { data: space } = useGetSpaceQuery();

  const currentHealthProGroup = space && healthProGroupForSpaceID(space.id);

  // TODO inefficient. Should pass the laneId as a composite object, a stringified JSON
  // e.g. "{'programmeID': '...', 'programmePlanID': '', 'dayID': ''}"

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: ownedProgrammesUnsorted } = useGetProgrammesQuery(undefined);

  const [createContentEntry] = useCreateContentEntryMutation();

  const [updateProgramme] = useUpdateProgrammeMutation();

  const ownedProgrammes = useMemo(() => {
    if (!ownedProgrammesUnsorted) {
      return [];
    }
    const ownedProgrammesSorted = [...ownedProgrammesUnsorted];
    ownedProgrammesSorted.sort(programmesComparer);
    return ownedProgrammesSorted;
  }, [ownedProgrammesUnsorted]);

  // console.log(JSON.stringify(props));

  const [popupMenuAnchorEl, setPopupMenuAnchorEl] = useState(null);

  const [advancedSearchAnchorEl, setAdvancedSearchAnchorEl] = useState(false);

  const handleClosePopupMenu = () => {
    setPopupMenuAnchorEl(null);
  };

  const programme = ownedProgrammes.find((pr) =>
    pr.plans.some((pl) => pl.days.some((d) => d.id === laneId)),
  );
  const plan = programme.plans.find((pl) =>
    pl.days.some((d) => d.id === laneId),
  );
  const arrayIndexWithinPlan = plan.days.findIndex((d) => d.id === laneId);
  const allDayIndexes = dayIndexesWithStartDay(1);
  const thisDayIndex = allDayIndexes[arrayIndexWithinPlan];

  const handleAdvancedSearch = () => {
    setAdvancedSearchAnchorEl(popupMenuAnchorEl);
    handleClosePopupMenu();
  };

  const handleCreateContentEntry = async () => {
    handleClosePopupMenu();
    const newContentEntry = {
      title: 'New lesson',
      tags: [],
    };
    try {
      const input = { ...newContentEntry };
      input.parentID = programme.id;
      input.groups = currentHealthProGroup ? [currentHealthProGroup] : null;
      const { data: contentEntry } = await createContentEntry(input);
      if (!contentEntry) {
        return;
      }
      const entryID = uuidv4();
      const updatedProgramme = programmeWithPlanUpdated(
        programme,
        programmePlanWithEntryAdded(
          plan,
          [laneId],
          entryID,
          EntryType.CONTENT_ENTRY,
          contentEntry.id,
        ),
      );
      tracker(
        userProfile,
        [
          {
            name: 'Add new content entry',
            args: {
              title: newContentEntry.title,
              section: 'programmes',
            },
          },
        ],
        ['numContentEntriesAdded'],
      );
      await updateProgramme(updatedProgramme);
      navigate(`/programmes/${programme.id}/plans/${plan.id}/entry/${entryID}`);
    } finally {
      reportAccountActivitySignalOperation({
        spaceID: space.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.PROGRAMMES,
        lastActivityObjectID: programme.id,
      });
    }
  };

  // Use as anchor element. For days at the right edge of the screen,
  // ensures the popover doesn't cover the box and therefore doesn't overlap
  // the lane.
  const boxRef = useRef();

  const recipesBoardIDs = programme.recipesBoardIDs || [];

  const onDismissAdvancedSearch = () => {
    setAdvancedSearchAnchorEl(null);
  };

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const onOwnMealSelected = async (mealID, title) => {
    // setLoading(true);
    console.log(`onOwnMealSelected ${mealID} ${title}`);
    tracker(userProfile, [
      {
        name: 'Meal selected',
        args: { 'title of meal selected': title },
      },
    ]);
    const dayIndexes = [thisDayIndex];
    const dayIDs = dayIndexes.map((dayIndex) =>
      programmePlanDayIDFromDayIndex(dayIndex, plan.days),
    );
    const entryID = uuidv4();
    const updatedPlan = programmePlanWithEntryAdded(
      plan,
      dayIDs,
      entryID,
      EntryType.MEAL,
      mealID,
    );
    const updatedProgramme = programmeWithPlanUpdated(programme, updatedPlan);
    await updateProgramme(updatedProgramme);
    reportAccountActivitySignalOperation({
      spaceID: space.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    onDismissAdvancedSearch();
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" ref={boxRef}>
        <Icon
          path={mdiPlusCircle}
          style={{
            width: '32px',
            height: '32px',
            cursor: 'pointer',
            color: theme.palette.primary.dark,
            marginTop: 6,
            justifyContent: 'flex-end',
          }}
          onClick={(ev) => {
            setPopupMenuAnchorEl(ev.target);
          }}
        />
      </Box>
      {advancedSearchAnchorEl && (
        <MealAdvancedSearchPopover
          section="programmes"
          anchorEl={advancedSearchAnchorEl}
          memoizedRecipesBoardIDs={recipesBoardIDs}
          initialSearchString=""
          onOwnMealSelected={onOwnMealSelected}
          onGoToQuickSearch={() => {}}
          onDismiss={onDismissAdvancedSearch}
        />
      )}
      {popupMenuAnchorEl && (
        <Menu anchorEl={popupMenuAnchorEl} open onClose={handleClosePopupMenu}>
          <PopupTitle
            titleText="Add Menu"
            closeButtonEnabled
            onClickClose={handleClosePopupMenu}
          />
          <MenuItem onClick={handleAdvancedSearch}>Search for meals</MenuItem>
          <MenuItem onClick={handleCreateContentEntry}>
            Create new lesson
          </MenuItem>
        </Menu>
      )}
    </>
  );
};

AddProgrammePlanCardLink.propTypes = {
  laneId: PropTypes.string.isRequired,
};

export default AddProgrammePlanCardLink;
